<script setup lang="ts">
    import { computed, onMounted } from 'vue';
    import { useEventCore } from './composable/useEventCore';
    import AppEventStatsRow from '@/pages/core/event/EventStatsRow.vue';
    import { useEventStore } from '@/stores/event';
    import { useTheme } from 'vuetify';
    import { EventPeriod } from '@/constants';

    const {
        periodId = EventPeriod.FullTime,
        isShowAllMarketsToggle = false,
        isAllExpanded = false,
        isShow = false,
    } = defineProps<{
        periodId?: number;
        isShowAllMarketsToggle?: boolean;
        isAllExpanded?: boolean;
        isShow?: boolean;
    }>();

    const eventStore = useEventStore();
    const theme = useTheme();
    const { t } = useI18n();

    const { isExpanded, isExpandedToggle } = useEventCore();

    onMounted(() => {
        console.log('Component %s mounted.', 'app-eventStats');
    });

    const emit = defineEmits(['toggleAllMarkets']);
    const toggleAllMarkets = () => emit('toggleAllMarkets');

    const isShowComp = computed(() => isShow);

    defineExpose({
        isShow: isShowComp,
        name: 'app-eventStats',
        periodId: periodId,
        isExpanded,
    });
</script>

<template>
    <v-card v-if="eventStore.stats" :style="theme.name.value === 'dark' ? 'background-color: #1e1f22' : ''" elevation="0" style="padding-bottom: 1px" tile>
        <v-card-title class="font13 pa-0 market-header d-flex align-center live">
            <v-icon size="32" class="pl-2" :icon="mdiChartBoxOutline" />
            <span :class="theme.name.value === 'dark' ? 'whiteText' : 'blackText'" class="pl-2 font-weight-regular">
                {{ t('eventStats.liveStats') }}
            </span>
            <v-spacer></v-spacer>
            <v-btn tag="a" v-if="isShowAllMarketsToggle" width="105" size="small" variant="outlined" color="tbGreen" class="mr-2 toggle-button font12" @click="toggleAllMarkets">
                <span v-if="isAllExpanded">{{ t('event.hideAll') }}</span>
                <span v-else>{{ t('event.showAll') }}</span>
            </v-btn>
            <v-icon v-if="isExpanded" class="mr-2" @click="isExpandedToggle()" :icon="mdiChevronUp" />
            <v-icon v-else class="mr-2" @click="isExpandedToggle()" :icon="mdiChevronDown" />
        </v-card-title>
        <v-card-text v-if="isExpanded" class="py-2">
            <AppEventStatsRow
                v-if="eventStore.stats.yellowHome !== undefined && eventStore.stats.yellowAway !== undefined && eventStore.stats.yellowHome !== null && eventStore.stats.yellowAway !== null"
                :header-text="t('eventStats.yellowCards')"
                :background-color="theme.name.value === 'dark' ? '#2c2f33' : '#FEF7DC'"
                :filled-color="theme.name.value === 'dark' ? '#E4AC01' : '#FEE8A4'"
                filled-darker-color="#FECA30"
                :value-one="eventStore.stats.yellowHome"
                :value-two="eventStore.stats.yellowAway"
            ></AppEventStatsRow>

            <AppEventStatsRow
                v-if="eventStore.stats.foulsHome !== undefined && eventStore.stats.foulsAway !== undefined && eventStore.stats.foulsHome !== null && eventStore.stats.foulsAway !== null"
                :header-text="t('eventStats.fouls')"
                :background-color="theme.name.value === 'dark' ? '#2c2f33' : '#E8E8E8'"
                :filled-color="theme.name.value === 'dark' ? '#545454' : '#A6A6A6'"
                filled-darker-color="#666666"
                :value-one="eventStore.stats.foulsHome"
                :value-two="eventStore.stats.foulsAway"
                numbers-class="orangeDarkText"
            ></AppEventStatsRow>

            <AppEventStatsRow
                v-if="
                    eventStore.stats.possessionTimeHome !== undefined &&
                    eventStore.stats.possessionTimeAway !== undefined &&
                    eventStore.stats.possessionTimeHome !== null &&
                    eventStore.stats.possessionTimeAway !== null
                "
                :header-text="t('eventStats.possession')"
                :background-color="theme.name.value === 'dark' ? '#2c2f33' : '#EDF5EA'"
                :filled-color="theme.name.value === 'dark' ? '#4D7939' : '#B7D6A8'"
                :filled-darker-color="theme.name.value === 'dark' ? '#6aa84f' : '#6AA84F'"
                :with-percent="true"
                :value-one="eventStore.stats.possessionTimeHome"
                :value-two="eventStore.stats.possessionTimeAway"
            ></AppEventStatsRow>

            <AppEventStatsRow
                v-if="eventStore.stats.attacksHome !== undefined && eventStore.stats.attacksAway !== undefined && eventStore.stats.attacksHome !== null && eventStore.stats.attacksAway !== null"
                :header-text="t('eventStats.attacks')"
                :background-color="theme.name.value === 'dark' ? '#2c2f33' : '#EDF5EA'"
                :filled-color="theme.name.value === 'dark' ? '#4D7939' : '#B7D6A8'"
                :filled-darker-color="theme.name.value === 'dark' ? '#6aa84f' : '#6AA84F'"
                :value-one="eventStore.stats.attacksHome"
                :value-two="eventStore.stats.attacksAway"
            ></AppEventStatsRow>

            <AppEventStatsRow
                v-if="
                    eventStore.stats.dangerousAttacksHome !== undefined &&
                    eventStore.stats.dangerousAttacksAway !== undefined &&
                    eventStore.stats.dangerousAttacksHome !== null &&
                    eventStore.stats.dangerousAttacksAway !== null
                "
                :header-text="t('eventStats.dangerousAttacks')"
                :background-color="theme.name.value === 'dark' ? '#2c2f33' : '#EDF5EA'"
                :filled-color="theme.name.value === 'dark' ? '#4D7939' : '#B7D6A8'"
                :filled-darker-color="theme.name.value === 'dark' ? '#6aa84f' : '#6AA84F'"
                :value-one="eventStore.stats.dangerousAttacksHome"
                :value-two="eventStore.stats.dangerousAttacksAway"
            ></AppEventStatsRow>

            <AppEventStatsRow
                v-if="eventStore.stats.shotsHome !== undefined && eventStore.stats.shotsAway !== undefined && eventStore.stats.shotsHome !== null && eventStore.stats.shotsAway !== null"
                :header-text="t('eventStats.shots')"
                :background-color="theme.name.value === 'dark' ? '#2c2f33' : '#EDF5EA'"
                :filled-color="theme.name.value === 'dark' ? '#4D7939' : '#B7D6A8'"
                :filled-darker-color="theme.name.value === 'dark' ? '#6aa84f' : '#6AA84F'"
                :value-one="eventStore.stats.shotsHome"
                :value-two="eventStore.stats.shotsAway"
            ></AppEventStatsRow>

            <AppEventStatsRow
                v-if="
                    eventStore.stats.shotsOnTargetHome !== undefined &&
                    eventStore.stats.shotsOnTargetAway !== undefined &&
                    eventStore.stats.shotsOnTargetHome !== null &&
                    eventStore.stats.shotsOnTargetAway !== null
                "
                :header-text="t('eventStats.shotsOnTarget')"
                :background-color="theme.name.value === 'dark' ? '#2c2f33' : '#EDF5EA'"
                :filled-color="theme.name.value === 'dark' ? '#4D7939' : '#B7D6A8'"
                :filled-darker-color="theme.name.value === 'dark' ? '#6aa84f' : '#6AA84F'"
                :value-one="eventStore.stats.shotsOnTargetHome"
                :value-two="eventStore.stats.shotsOnTargetAway"
            ></AppEventStatsRow>

            <AppEventStatsRow
                v-if="
                    eventStore.stats.hitWoodworkHome !== undefined &&
                    eventStore.stats.hitWoodworkAway !== undefined &&
                    eventStore.stats.hitWoodworkHome !== null &&
                    eventStore.stats.hitWoodworkAway !== null
                "
                :header-text="t('eventStats.hitWoodwork')"
                :background-color="theme.name.value === 'dark' ? '#2c2f33' : '#EDF5EA'"
                :filled-color="theme.name.value === 'dark' ? '#4D7939' : '#B7D6A8'"
                :filled-darker-color="theme.name.value === 'dark' ? '#6aa84f' : '#6AA84F'"
                :value-one="eventStore.stats.hitWoodworkHome"
                :value-two="eventStore.stats.hitWoodworkAway"
            ></AppEventStatsRow>

            <AppEventStatsRow
                v-if="eventStore.stats.cornersHome !== undefined && eventStore.stats.cornersAway !== undefined && eventStore.stats.cornersHome !== null && eventStore.stats.cornersAway !== null"
                :header-text="t('eventStats.corners')"
                :background-color="theme.name.value === 'dark' ? '#2c2f33' : '#EDF5EA'"
                :filled-color="theme.name.value === 'dark' ? '#4D7939' : '#B7D6A8'"
                :filled-darker-color="theme.name.value === 'dark' ? '#6aa84f' : '#6AA84F'"
                :value-one="eventStore.stats.cornersHome"
                :value-two="eventStore.stats.cornersAway"
            ></AppEventStatsRow>
        </v-card-text>
    </v-card>
</template>

<style scoped>
    @import url('./styleMarketsCore.css');
</style>
